import { useQuery } from '@tanstack/react-query';
import { createQueryHook } from './create-query-hook';
import { queryKeys } from '@/lib/query-keys';
import { executeQuery } from '@/lib/execute-query';
import { Action } from '@/business/types/action.types';
import StorageService from '@/utils/storage';
import { StorageKey } from '@/types/storage';
import type {
  GetTaskInput,
  GetTaskAssignmentsInput,
  GetTaskAssignmentsOutput,
  GetTaskRelationsInput,
  GetTaskRelationsOutput,
  GetCommentsInput,
  GetCommentsOutput,
  GetOrganizationUsersInput,
  GetOrganizationUsersOutput,
  GetAllOrganizationsInput,
  GetAllOrganizationsOutput,
  GetBoardPreferencesInput,
  GetBoardPreferencesOutput,
  GetBoardTemplatesInput,
  GetBoardTemplatesOutput,
  GetOrgConfigurationsInput,
  GetOrgConfigurationsOutput,
  GetUserGroupsInput,
  GetUserGroupsOutput,
  GetCohortBoardInput,
  GetCohortBoardOutput,
  GetScheduledJobsInput,
  GetScheduledJobsOutput,
  GetJobRunsInput,
  GetJobRunsOutput,
  GetUserGroupMembersInput,
  GetUserGroupMembersOutput,
  GetUserGroupMembersBulkInput,
  GetUserGroupMembersBulkOutput,
  ListProfilePicturesInput,
  ListProfilePicturesOutput,
  ListOrgProfilePicturesInput,
  ListOrgProfilePicturesOutput,
  ListBoardBackgroundsInput,
  ListBoardBackgroundsOutput,
} from '@/types/generated';
import type { GetCardUIOutput } from '@/types/action-outputs';

export const useCardQuery = createQueryHook<
  string | undefined,
  GetTaskInput,
  GetCardUIOutput
>({
  action: Action.GET_TASK,
  queryKey: taskId => queryKeys.task.detail(taskId!),
  buildInput: taskId => ({ task_id: taskId! }),
  staleTime: 1000 * 60,
  enabled: taskId => !!taskId,
});

export const useTaskAssignmentsQuery = createQueryHook<
  string | undefined,
  GetTaskAssignmentsInput,
  GetTaskAssignmentsOutput
>({
  action: Action.GET_TASK_ASSIGNMENTS,
  queryKey: taskId => queryKeys.taskAssignments.forTask(taskId!),
  buildInput: taskId => ({ task_id: taskId! }),
  staleTime: 1000 * 60 * 2,
  enabled: taskId => !!taskId,
});

export const useTaskRelationsQuery = createQueryHook<
  string | undefined,
  GetTaskRelationsInput,
  GetTaskRelationsOutput
>({
  action: Action.GET_TASK_RELATIONS,
  queryKey: taskId => queryKeys.taskRelations.forTask(taskId!),
  buildInput: taskId => ({ task_id: taskId! }),
  staleTime: 1000 * 60 * 2,
  enabled: taskId => !!taskId,
});

export const useOrgUsersQuery = createQueryHook<
  string | undefined,
  GetOrganizationUsersInput,
  GetOrganizationUsersOutput
>({
  action: Action.GET_ORGANIZATION_USERS,
  queryKey: orgId => queryKeys.orgUsers.forOrg(orgId!),
  buildInput: orgId => ({ organization_id: orgId! }),
  staleTime: 1000 * 60 * 5,
  enabled: orgId => !!orgId,
});

export const useBoardPreferencesQuery = createQueryHook<
  string | undefined,
  GetBoardPreferencesInput,
  GetBoardPreferencesOutput
>({
  action: Action.GET_BOARD_PREFERENCES,
  queryKey: boardId => queryKeys.boardPreferences.forBoard(boardId!),
  buildInput: boardId => ({ board_id: boardId! }),
  staleTime: 1000 * 60 * 5,
  enabled: boardId => !!boardId,
});

export const useAllOrgsQuery = createQueryHook<
  boolean | undefined,
  GetAllOrganizationsInput,
  GetAllOrganizationsOutput
>({
  action: Action.GET_ALL_ORGANIZATIONS,
  queryKey: includeDeleted =>
    includeDeleted
      ? ([...queryKeys.allOrgs.all(), 'include_deleted'] as const)
      : queryKeys.allOrgs.all(),
  buildInput: includeDeleted =>
    includeDeleted ? { include_deleted: true } : {},
  staleTime: 1000 * 60 * 5,
});

export function useCommentsQuery(
  taskId: string | undefined,
  offset: number = 0
) {
  return useQuery({
    queryKey: queryKeys.comments.page(taskId!, offset),
    queryFn: () =>
      executeQuery<GetCommentsInput, GetCommentsOutput>(Action.GET_COMMENTS, {
        task_id: taskId!,
        offset,
      }),
    staleTime: 1000 * 30,
    enabled: !!taskId,
  });
}

export const useOrgConfigurationsQuery = createQueryHook<
  string | undefined,
  GetOrgConfigurationsInput,
  GetOrgConfigurationsOutput
>({
  action: Action.GET_ORG_CONFIGURATIONS,
  queryKey: orgId => queryKeys.orgConfigurations.forOrg(orgId!),
  buildInput: orgId => ({ organization_id: orgId! }),
  staleTime: 1000 * 60 * 5,
  enabled: orgId => !!orgId,
});

export const useBoardTemplatesQuery = createQueryHook<
  { orgId: string | undefined; includeDeleted?: boolean },
  GetBoardTemplatesInput,
  GetBoardTemplatesOutput
>({
  action: Action.GET_BOARD_TEMPLATES,
  queryKey: ({ orgId, includeDeleted }) =>
    includeDeleted
      ? ([
          ...queryKeys.boardTemplates.forOrg(orgId!),
          'include_deleted',
        ] as const)
      : queryKeys.boardTemplates.forOrg(orgId!),
  buildInput: ({ orgId, includeDeleted }) => ({
    organization_id: orgId!,
    ...(includeDeleted ? { include_deleted: true } : {}),
  }),
  staleTime: 1000 * 60 * 5,
  enabled: ({ orgId }) => !!orgId,
  refetchOnWindowFocus: false,
});

export const useUserGroupsQuery = createQueryHook<
  string | undefined,
  GetUserGroupsInput,
  GetUserGroupsOutput
>({
  action: Action.GET_USER_GROUPS,
  queryKey: orgId => queryKeys.userGroups.forOrg(orgId!),
  buildInput: orgId => ({ organization_id: orgId! }),
  staleTime: 1000 * 60 * 2,
  enabled: orgId => !!orgId,
});

export const useCohortBoardQuery = createQueryHook<
  { group_id?: string; organization_id?: string },
  GetCohortBoardInput,
  GetCohortBoardOutput
>({
  action: Action.GET_COHORT_BOARD,
  queryKey: ({ group_id, organization_id }) =>
    queryKeys.cohortBoard.forGroup(group_id!, organization_id!),
  buildInput: ({ group_id, organization_id }) => ({
    group_id: group_id!,
    organization_id: organization_id!,
  }),
  staleTime: 1000 * 30,
  enabled: ({ group_id, organization_id }) => !!group_id && !!organization_id,
});

export const useScheduledJobsQuery = createQueryHook<
  string | undefined,
  GetScheduledJobsInput,
  GetScheduledJobsOutput
>({
  action: Action.GET_SCHEDULED_JOBS,
  queryKey: orgId => queryKeys.scheduledJobs.forOrg(orgId!),
  buildInput: orgId => ({ organization_id: orgId! }) as GetScheduledJobsInput,
  staleTime: 1000 * 30,
  refetchOnWindowFocus: false,
  enabled: orgId => !!orgId,
});

export const useJobRunsQuery = createQueryHook<
  string | undefined,
  GetJobRunsInput,
  GetJobRunsOutput
>({
  action: Action.GET_JOB_RUNS,
  queryKey: jobId => queryKeys.jobRuns.forJob(jobId!),
  buildInput: jobId => ({ job_id: jobId!, page: 1, page_size: 10 }),
  staleTime: 1000 * 15,
  enabled: jobId => !!jobId,
  refetchOnWindowFocus: false,
});

export const useUserGroupMembersQuery = createQueryHook<
  string | undefined,
  GetUserGroupMembersInput,
  GetUserGroupMembersOutput
>({
  action: Action.GET_USER_GROUP_MEMBERS,
  queryKey: groupId => queryKeys.userGroupMembers.forGroup(groupId!),
  buildInput: groupId => ({ group_id: groupId! }),
  staleTime: 1000 * 60,
  enabled: groupId => !!groupId,
});

export const useUserGroupMembersBulkQuery = createQueryHook<
  string[] | undefined,
  GetUserGroupMembersBulkInput,
  GetUserGroupMembersBulkOutput
>({
  action: Action.GET_USER_GROUP_MEMBERS_BULK,
  queryKey: groupIds => queryKeys.userGroupMembers.forGroups(groupIds ?? []),
  buildInput: groupIds => ({ group_ids: groupIds ?? [] }),
  staleTime: 1000 * 60,
  enabled: groupIds => !!groupIds?.length,
});

export function useProfilePicturesQuery() {
  const cached = StorageService.get<ListProfilePicturesOutput['pictures']>(
    StorageKey.PROFILE_PICTURES
  );
  return useQuery<ListProfilePicturesOutput>({
    queryKey: queryKeys.profilePictures.all(),
    queryFn: () =>
      executeQuery<ListProfilePicturesInput, ListProfilePicturesOutput>(
        Action.LIST_PROFILE_PICTURES,
        {}
      ),
    staleTime: 1000 * 60 * 5,
    ...(cached ? { initialData: { pictures: cached } } : {}),
  });
}

export function useOrgProfilePicturesQuery(organizationId: string | undefined) {
  const cached = StorageService.get<ListOrgProfilePicturesOutput['pictures']>(
    StorageKey.ORG_PROFILE_PICTURES
  );
  return useQuery<ListOrgProfilePicturesOutput>({
    queryKey: queryKeys.orgProfilePictures.all(),
    queryFn: () =>
      executeQuery<ListOrgProfilePicturesInput, ListOrgProfilePicturesOutput>(
        Action.LIST_ORG_PROFILE_PICTURES,
        { organization_id: organizationId! }
      ),
    staleTime: 1000 * 60 * 5,
    enabled: !!organizationId,
    ...(cached ? { initialData: { pictures: cached } } : {}),
  });
}

export function useBoardBackgroundsQuery(boardId: string | undefined) {
  const allCached = StorageService.get<
    Record<string, ListBoardBackgroundsOutput['backgrounds']>
  >(StorageKey.BOARD_BACKGROUNDS);
  const cached = boardId ? allCached?.[boardId] : undefined;
  return useQuery<ListBoardBackgroundsOutput>({
    queryKey: queryKeys.boardBackgrounds.forBoard(boardId!),
    queryFn: () =>
      executeQuery<ListBoardBackgroundsInput, ListBoardBackgroundsOutput>(
        Action.LIST_BOARD_BACKGROUNDS,
        { board_id: boardId! }
      ),
    staleTime: 1000 * 60 * 5,
    enabled: !!boardId,
    ...(cached ? { initialData: { backgrounds: cached } } : {}),
  });
}
